import { useCallback, useEffect, useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Column } from 'devextreme-react/data-grid';
import useMainProgressContext from '../../../../components/progress/MainProgress/context/MainProgressContext';
import useDataGridProgressContext from '../../../../components/progress/DataGridProgress/context/DataGridProgressContext';
import * as Selectors from '../../redux/usersSelectors';
import * as Actions from '../../redux/usersActions';
import { API_REQUEST_STATE } from '../../../../core/enums/apiRequestState';
import DataGrid from '../../../../components/DataGrid/DataGrid';
import SnackbarSettings from '../../../../components/Snackbar/SnackbarSettings';
import { DataGridCellRender } from '../../../../components/DataGrid/model/dataGridCellRenderModel';
import DataGridActionTemplate from '../../../../components/DataGrid/template/DataGridActionTemplate';
import { IUser } from '../../model/usersModel';
import ActionConfirmationDialog from '../../../../components/ConfirmationDialog';

const UsersDataGrid: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mainProgressContext = useMainProgressContext();
  const dataGridProgressContext = useDataGridProgressContext();
  const allUsers = useSelector(Selectors.allUsers);
  const allUsersState = useSelector(Selectors.apiAllUsersRequestState);
  const apiRequestState = useSelector(Selectors.apiUsersRequestState);
  const apiRequestError = useSelector(Selectors.apiUsersRequestError);
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState('');

  const handleActionClick = useCallback((id: string) => {
    setOpen(true);
    setUserId(id);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    dataGridProgressContext.show();
    dispatch(Actions.deleteUsers({ id: userId }));
    setOpen(false);
  }, [userId]);

  const handleDialogClose = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    dataGridProgressContext.show();
    dispatch(Actions.getAllUsers());
  }, []);

  useEffect(() => {
    mainProgressContext.hide();
  }, [allUsers]);

  useEffect(() => {
    if (allUsersState === API_REQUEST_STATE.SUCCEEDED_GET) {
      setTimeout(() => {
        dataGridProgressContext.hide();
      }, 500);
    }
  }, [allUsersState]);

  return (
    <>
      <DataGrid rows={allUsers}>
        <Column dataField="id" caption={t('users.admins.id')} width="10%" minWidth="100" />
        <Column dataField="username" caption={t('users.admins.username')} width="80%" minWidth="300" />
        <Column
          dataField="actions"
          caption=""
          width="10%"
          allowSorting={false}
          minWidth="100"
          cellRender={(cellData: DataGridCellRender<IUser>) => {
            return <DataGridActionTemplate onClick={() => handleActionClick(cellData.data.id!)} />;
          }}
        />
      </DataGrid>
      <ActionConfirmationDialog
        open={open}
        onConfirm={handleDeleteConfirm}
        onClose={handleDialogClose}
        title={'users.admins.confirmation.confirmation'}
        text={'users.admins.confirmation.delete'}
      />
      <SnackbarSettings
        successMessage="users.admins.snackbar.delete_success"
        apiRequestState={apiRequestState}
        apiRequestError={apiRequestError}
        setApiRequestState={Actions.setApiUsersRequestState}
        onSuccess={() => {
          dispatch(Actions.getAllUsers());
        }}
        onError={() => {
          dataGridProgressContext.hide();
        }}
      />
    </>
  );
};

export default UsersDataGrid;
